import { styled, TextField, TextFieldProps } from "@mui/material"

import { Logic } from "@cirrus/fe-lib"

import { capitalize, getInputPadding } from "./lib"

export type StyledTextFieldProps = TextFieldProps & {
  background?: string
  backgroundOnHover?: string
  disableUnderline?: boolean
  width?: string | number,
  height?: number
} & {
  theme?: any
}

export const StyledTextField = styled(TextField)<StyledTextFieldProps>(
  ({
     background = "#f1f3f4",
     backgroundOnHover = "#f1f3f4",
     error,
     hiddenLabel,
     label,
     multiline,
     size,
     theme: { typography, withPalette, palette },
     variant = "filled",
     width = 320,
     height: height_,
     disableUnderline
   }) => {
    const height: { minHeight?: string | number; height?: string | number } = {
      [multiline ? "minHeight" : "height"]:
        size === "small" ? typography.pxToRem(38) : typography.pxToRem(height_ || 44),
      ...(multiline && !hiddenLabel ? { minHeight: typography.pxToRem(52) } : {})
    }

    if (multiline) delete height.height

    return {
      [`& .Mui${capitalize(variant)}Input-root`]: {
        "& input:focus::placeholder": {
          display: "none",
          color: "transparent"
        },
        ["& textarea.MuiInputBase-input"]: {
          padding: 0,
          paddingRight: "8px",
          marginRight: "8px",
          overflowY: "auto",

          ...scrollBar
        },
        [`& .Mui${capitalize(variant)}Input-input`]: {
          fontSize: size === "small" ? typography.pxToRem(12) : typography.pxToRem(15),
          height: size === "small" ? typography.pxToRem(18) : typography.pxToRem(22),
          ...getInputPadding({ label, size, typography, variant })
        },
        "&[class*=MuiFilledInput-root]": {
          "&:after, &:before, &:hover:not(.Mui-disabled, .Mui-error):before": {
            borderBottomStyle: disableUnderline ? "none" : "inset",
          },
          "&.Mui-disabled:before": {
            borderBottomStyle: disableUnderline ? "none" : "dotted"
          }
        },
        "&.Mui-disabled": {
          backgroundColor: withPalette(background)
        },
        "&.Mui-focused": {
          backgroundColor: withPalette(backgroundOnHover),
          "& input::placeholder, & textarea::placeholder": {
            opacity: 0
          }
        },
        "&:after": {
          borderColor: error ? palette.error.main : palette.primary.main,
        },
        "&:hover": {
          backgroundColor: withPalette(backgroundOnHover)
        },
        backgroundColor: withPalette(background),
        borderRadius: disableUnderline ? "4px" : "4px 4px 0 0",
        width,

        ...height,

        ...variant === "outlined" && outlined,

        ...multiline && {
          padding: "1.6rem 2rem",

          "& .MuiInputAdornment-root": {
            alignSelf: "baseline",
            height: "auto",
            color: "#333333"
          },
        },

        "& .MuiSvgIcon-root": {
          ...!multiline && {
            color: "#737373",
            fontSize: "18px !important"
          }
        }
      },
      "& .MuiFormHelperText-root": {
        marginLeft: 0
      },
      "& .MuiAutocomplete-tag": {
        marginTop: Logic.consequence(true, !!hiddenLabel, undefined, typography.pxToRem(20))
      },
      "& .MuiInputLabel-root": {
        "&:not([class*='MuiFormLabel-shrink'])": {
          marginTop: "-0.4rem"
        }
      },
      "& input": {
        "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
          "-webkit-appearance": "none",
          margin: 0
        },
        color: !!error ? palette.error.main : palette.text.primary,
      },
      "& .Mui-readOnly": {
        cursor: "default",
      }
    }
  }
)

const outlined = {
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: 0
  },

  "&.Mui-focused": {
    "&:hover": {
      backgroundColor: "#fff"
    },

    backgroundColor: "#fff",

    "& .MuiOutlinedInput-notchedOutline": {
      borderWidth: "1px",
      borderColor: "#79747E"
    },
    "& input::placeholder": {
      opacity: 0
    }
  }
}

const scrollBar = {
  "&::-webkit-scrollbar": {
    width: "14px"
  },

  "&::-webkit-scrollbar-thumb": {
    background: "#757575",
    borderRadius: "8px",
    border: "4px solid #d3d3d3"
  },

  "&::-webkit-scrollbar-track": {
    background: "#d3d3d3"
  }
}
